<template lang="pug">
.supercharged-entertainment-container
  gift-card-preview(v-if="giftCardPreviewing" :is-edit-mode="editMode" @add-to-cart="addCardToCartAndGo")
  .page-gift-card.book-race(v-else)
    .book-form
      form(@submit.prevent="processCard")
        agree-to-terms.book-form__agreement(v-model="termsAgreed")
        re-group-box.book-form__tab.giftcard-amount(:class="{ disabled: termsAgreed != 'yes' }")
          template(slot="title") Select your amount
          span.book-form__tab__number 1
          p.info-note Please choose the gift card amount.
          label Amount
          .selector.flex-rows
            select.flex-child-grow(v-model="productId" @input="$v.productId.$reset")
              option(value="")
              option(v-for="{productId, title} in _.sortBy(Object.values(availableGiftCards), ['order', 'title'])" :value="productId") {{ title }}
        re-content-placeholder(name="purchase_card_below_product_selection")
        re-group-box.book-form__tab(:class="{ disabled: disabledPersonalDetails }")
          template(slot="title") Personal information
          span.book-form__tab__number 2
          p.book-form__tab__notice Please enter your contact details.
          .book-form__align
            div
              label First Name
              input.book-form__input-full(v-model="firstName" type="text" @input="$v.firstName.$reset")
            div
              label Last Name
              input.book-form__input-full(v-model="lastName" type="text" @input="$v.lastName.$reset")
            template(v-if="useEmail && showEmailBeforePhone")
              div
                label Email Address
                input.book-form__input-full(v-model="email" type="text" @input="$v.email.$reset")
              div(v-if="useConfirmation")
                label Confirm Email Address
                input.book-form__input-full(v-model="emailConfirmation" type="text" @input="$v.emailConfirmation.$reset")
            div
              label Phone Number
              input.book-form__input-full(v-model="phone" type="text" @input="$v.phone.$reset")
            div(v-if="useConfirmation")
              label Confirm Phone Number
              input.book-form__input-full(v-model="phoneConfirmation" type="text" @input="$v.phoneConfirmation.$reset")
            template(v-if="useEmail && !showEmailBeforePhone")
              div
                label Email Address
                input.book-form__input-full(v-model="email" type="text" @input="$v.email.$reset")
              div(v-if="useConfirmation")
                label Confirm Email Address
                input.book-form__input-full(v-model="emailConfirmation" type="text" @input="$v.emailConfirmation.$reset")
        re-group-box.book-form__tab(:class="{ disabled: disabledGifteeBox }")
          template(slot="title") This Gift Card is for (recipient)
          span.book-form__tab__number 3
          p.book-form__tab__notice Please enter contact details.
          .book-form__tab__radio
            label.radio-container(for="send_me")
              .p-l-5.p-t-1 Send it to me
              input#send_me(type="radio" value="" v-model="giftee")
              span.css-radio
            label.radio-container(for="send_someone")
              .p-l-5.p-t-1 Send it to someone else
              input#send_someone(type="radio" value="_recipient" v-model="giftee")
              span.css-radio
          .book-form__align(:class="{ 'book-form__align-hide': disabledGifteeDetails }")
            div
              label First Name
              input.book-form__input-full(v-model="gifteeFirstName" type="text" @input="$v.gifteeFirstName.$reset")
            div
              label Last Name
              input.book-form__input-full(v-model="gifteeLastName" type="text" @input="$v.gifteeLastName.$reset")
            div
              label Email Address
              input.book-form__input-full(v-model="gifteeEmail" type="text" @input="$v.gifteeEmail.$reset")
            div
              label Confirm Email Address
              input.book-form__input-full(v-model="gifteeEmailConfirmation" type="text" @input="$v.gifteeEmailConfirmation.$reset")
            div
              label Phone Number
              input.book-form__input-full(v-model="gifteePhone" type="text" @input="$v.gifteePhone.$reset")
            div
              label Confirm Phone Number
              input.book-form__input-full(v-model="gifteePhoneConfirmation" type="text" @input="$v.gifteePhoneConfirmation.$reset")
        re-group-box.book-form__tab(v-if="giftee" :class="{ disabled: disabledCardProcessor }")
          template(slot="title") Congratulate your recipient!
          span.book-form__tab__number 4
          p.book-form__tab__notice Please enter the congratulatory text or other note.
          label Note
          input.book-form__input-full(v-model="giftNote" type="text" placeholder='Ex, "Happy Birthday"')

        would-you-like-to.m-t-30(v-if="!editMode" exclude="racewaybooking.gift-card" @before-route="likeClick")

        .book-form__add-to-cart
          button.site_button.site_button_inverse.show-email(
              v-if="giftee"
              @click.prevent="processCard"
              :class="{ disabled: disabledCardProcessor }"
            )
            span Next
          v-button.site_button.site_button_inverse(
            v-else
            @click="addCardToCartAndGo"
            :class="{ disabled: disabledCardProcessor }"
            :loading="isAddingToCart"
            )
            span {{ editMode ? 'Update Cart' : 'Add to Cart' }}

</template>

<script>
import { mapGetters } from 'vuex'

import PageMixins from '../../components/_mixins/pages'
import CartMixins from '../../components/_mixins/location-checkout'
import CardMixins from '../../components/_mixins/giftcard'
import ScrollMixins from '../../components/_mixins/scroller'
import AgreeToTerms from '../../components/parts/AgreeToTerms'
import GiftCardPreview from './preview'
import WouldYouLikeTo from '../../components/parts/would-you-like-to'

import {ValidationMixins, setValidationRule, parseValidationSetup, parseValidations, Validators} from '../../lib/validations'
import {getQS} from '../../lib/utils'
const qs = getQS() || {}

const EventBus = window.VueEventBus

const validations = {
  productId: setValidationRule('productId', {
    required: {error: 'Select gift card amount.'}
  }),

  firstName: setValidationRule('firstName'),
  lastName: setValidationRule('lastName'),

  email: setValidationRule('email', {
    required: {error: 'Email is required.'},
    email: {error: 'Email is not valid.'},
  }),
  emailConfirmation: setValidationRule('emailConfirmation', {
    confirmed: {error: 'Email is not confirmed.', rule: Validators.sameAsCI('email')},
  }),

  phone: setValidationRule('phone'),
  phoneConfirmation: setValidationRule('phoneConfirmation', {
    confirmed: {error: 'Phone is not confirmed.', rule: Validators.sameAs('phone')},
  }),

  gifteeFirstName: setValidationRule('gifteeFirstName', {
    required: {error: 'Recipient\'s first name is required.', rule: Validators.requiredIf('giftee')}
  }),
  gifteeLastName: setValidationRule('gifteeLastName', {
    required: {error: 'Recipient\'s last name is required.', rule: Validators.requiredIf('giftee')}
  }),

  gifteeEmail: setValidationRule('gifteeEmail', {
    required: {error: 'Recipient\'s email is required.', rule: Validators.requiredIf('giftee')},
    email: {error: 'Recipient\'s email is not valid.'},
  }),
  gifteeEmailConfirmation: setValidationRule('gifteeEmailConfirmation', {
    confirmed: {error: 'Email is not confirmed.', rule: Validators.sameAsCI('gifteeEmail')},
  }),

  gifteePhone: setValidationRule('gifteePhone', {
    required: {error: 'Recipient\'s phone is required.', rule: Validators.requiredIf('giftee')}
  }),
  gifteePhoneConfirmation: setValidationRule('gifteePhoneConfirmation', {
    confirmed: {error: 'Phone is not confirmed.', rule: Validators.sameAs('gifteePhone')},
  }),
}

export default {
  name: 'page-gift-card',
  settings: 'page.buy-giftcard',
  translations: 'page.buy-giftcard',
  components: {
    AgreeToTerms,
    GiftCardPreview,
    WouldYouLikeTo,
  },

  mixins: [PageMixins, CartMixins, ValidationMixins, CardMixins, ScrollMixins],

  data: () => ({
    editMode: false,
    editItem: null,
    count: 1,
    termsAgreed: 'no',
    emailConfirmation: null,
    phoneConfirmation: null,
    gifteeEmailConfirmation: null,
    gifteePhoneConfirmation: null,
    validations: {},
    isAddingToCart: false,

    // copied from components/_mixins/personal-details.js
    useEmail: true,
    useConfirmation: false,
    showEmailBeforePhone: true,
  }),

  validations() {
    const personalDetails = ['firstName', 'lastName', 'phone']
    if (this.useConfirmation) {
      personalDetails.push('phoneConfirmation')
    }
    if (this.useEmail) {
      personalDetails.push('email')
      if (this.useConfirmation) {
        personalDetails.push('emailConfirmation')
      }
    }
    const gifteeDetails = [
      'gifteeFirstName',
      'gifteeLastName',
      'gifteeEmail',
      'gifteeEmailConfirmation',
      'gifteePhone',
      'gifteePhoneConfirmation'
    ]
    return {
      ...parseValidations(this.parsableValidations),
      personalDetails,
      gifteeDetails
    }
  },
  computed: {
    ...mapGetters({
      user: 'authUser',
      authenticated: 'authCheck',
    }),
    parsableValidations() {
      let allValidations = _.cloneDeep(validations)
      if (!this.useEmail) {
        allValidations = _.omit(allValidations, ['email', 'emailConfirmation'])
      }
      if (!this.useConfirmation) {
        allValidations = _.omit(allValidations, ['phoneConfirmation', 'emailConfirmation'])
      }
      return allValidations
    },
    disabledPersonalDetails() {
      return !this.productId
    },
    disabledGifteeBox() {
      return this.disabledPersonalDetails || this.$v.personalDetails.$invalid
    },
    disabledGifteeDetails() {
      return !this.giftee
    },
    disabledCardProcessor() {
      return this.disabledGifteeBox || (this.giftee && this.$v.gifteeDetails.$invalid)
    },

    selectedProduct() {
      const searchId = this.productId
      if (searchId) {
        return _.find(this.availableGiftCards, (card) => card.id == searchId)
      }
      return null
    },
  },

  watch: {
    productId(newVal) {
      EventBus.$emit('buy-card.change.productId', newVal)
      EventBus.$emit('buy-card.change.product', this.selectedProduct)
      EventBus.$emit('buy-card.change.product-with-location', {
        product: this.selectedProduct,
        location_id: this.currentLocationId,
      })
    },
  },

  async created() {
    if (this.authenticated) {
      this.firstName = this.user.first_name
      this.lastName = this.user.last_name
      this.email = this.emailConfirmation = this.user.email
      this.phone = this.phoneConfirmation = this.user.meta.phone
    }

    this.validations = parseValidationSetup(this.parsableValidations, this)

    if (qs.item || this.$route.query.item) {
      await this.setEditItem(qs.item || this.$route.query.item)
    }
  },

  methods: {
    async setEditItem(id) {
      if (this.cartItems[id] && this.cartItems[id].type === 'gift_cards') {
        this.editItem = this.cartItems[id]
        this.editMode = true
        await this.hydrateCardFromCart(this.editItem)
      }
    },
    processCard() {
      if (this.disabledCardProcessor) {
        this.scrollToFirstErrorElement()
      } else {
        this.showGiftCardPreview()
      }
    },

    async hydrateCardFromCart(cartItem) {
      this.setGiftCardInEditMode()
      const itemPayload = cartItem.payload
      const {options: {inputs = {}} = {}} = itemPayload

      this.termsAgreed = 'yes'
      this.count = itemPayload.count

      await this.setGiftCardInput(inputs)

      this.phoneConfirmation = this.phone
      this.emailConfirmation = this.email
      this.gifteeEmailConfirmation = this.gifteeEmail
      this.gifteePhoneConfirmation = this.gifteePhone
    },

    goBackToCardShop(){
      this.goto({page: 'page-gift-card', noScroll: false})
    },

    async addCardToCartAndGo(e) {
      if (e) {
        e.preventDefault()
      }
      await this.addCardToCart()
      this.$router.push({ name: 'racewaybooking.checkout' })
    },

    async addCardToCart() {
        this.cardReadyForCart = true
        const cartItem = this.proposedGiftCardCart
        if (this.currentLocationId) {
          _.set(cartItem, 'payload.options.location_id', this.currentLocationId)
        }
        cartItem.payload.count = this.count
        this.isAddingToCart = true
        await this.addCartItem(cartItem)
    },

    likeClick() {
      if (!this.disabledCardProcessor) {
        this.addCardToCart()
      }
    },
  },
}
</script>

<style lang="scss">
@import '../../components/_scss/base';
@import '../../components/_scss/pages/giftcard';

.supercharged-entertainment-container .page-gift-card {
  .radio-container {
    margin-bottom: unset;
  }

  .book-form {
    padding-bottom: 85px;
  }

  .book-form__tab.giftcard-amount .selector {
    width: 50%;
    @media screen and (max-width: 770px) {
      width: 100%
    }
  }

  @media screen and (max-width: 770px) {
    .book-form__tab__radio label {
      padding-bottom: 24px;
    }
  }
}
</style>
